import { InputBase, styled } from '@mui/material'
import { ValuePanelInputRoot } from './commons'
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Tooltip from '../../../../../components/tooltips/Tooltip'
import { useDebounce } from '../../../../../hooks/useDebounce'
import { intl } from '../../../../../../i18n'
import { colorPalette } from '../../../../../style/colorPalette'
import { useNumberParser } from '../../../../../hooks/useNumberParser'

type HourInputProps = {
  label: string
  value: number | undefined
  onChange?: (v: number | undefined) => void
  editable?: boolean
}
export const HourInput = ({
  label,
  value,
  onChange,
  editable = true,
}: HourInputProps) => {
  const [inputValue, setInputValue] = useState('')
  const changedByInput = useRef(false)
  const hourUnitLabel = useMemo(
    () => intl.formatMessage({ id: 'singleSheet.header.hours' }),
    []
  )
  const [isComposing, setIsComposing] = useState(false)

  useEffect(() => {
    changedByInput.current = false
    setInputValue(typeof value === 'number' ? value + '' : '')
  }, [value])

  const { round, parse } = useNumberParser()

  const onChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    changedByInput.current = true
    setInputValue(e.target.value)
  }, [])
  const debouncedInputValue = useDebounce(inputValue, 300)
  useEffect(() => {
    if (isComposing) return
    if (!changedByInput.current) return
    const normalized = parse(debouncedInputValue)
    const newValue = round(normalized)
    onChange && onChange(newValue)
    setInputValue(typeof newValue === 'number' ? newValue + '' : '')
  }, [debouncedInputValue, parse, round, onChange, isComposing])

  const onCompositionStart = useCallback(() => {
    setIsComposing(true)
  }, [])
  const inputValueRef = useRef<string>('')
  useEffect(() => {
    inputValueRef.current = inputValue
  }, [inputValue])
  const onCompositionEnd = useCallback(e => {
    setIsComposing(false)
    changedByInput.current = true
    setInputValue(inputValueRef.current)
  }, [])

  return (
    <Tooltip message={label}>
      <Root>
        <TextInput
          value={inputValue}
          onChange={onChangeInput}
          disabled={!editable}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          placeholder="0"
        />
        <HourUnitLabel>{hourUnitLabel}</HourUnitLabel>
      </Root>
    </Tooltip>
  )
}

const Root = styled(ValuePanelInputRoot)({
  width: '60px',
})
const TextInput = styled(InputBase)({
  height: '10px',
  '& .MuiInputBase-input': {
    width: '100%',
    textAlign: 'right',
    padding: 0,
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: colorPalette.monotone[10],
    '-webkit-text-fill-color': colorPalette.monotone[10],
  },
})
const HourUnitLabel = styled('span')({
  whiteSpace: 'nowrap',
  fontSize: '11px',
})
